import { Download, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { ReactNode } from 'react';
import { Button } from '~/components/ui/button';
import { cn, formatFileSize } from '~/lib/utils';

/** The four facts a tile needs. Its callers' attachments carry more than this
 *  and none of them carries the same extra, so the shape is what they share. */
export type Attachment = {
  contentType: string;
  filename: string;
  size: number | null;
  url: string | null;
};

/**
 * What came with the mail, as the design's tiles: a preview if we can draw one,
 * the extension if we cannot, and one action on the corner of both.
 *
 * `onPrimary` is which surface the tiles are sitting on, not who sent the mail —
 * a mail thread has the same attachments and no bubble. Filtering is the
 * caller's: the feed drops inline images server-side, the mailbox on the way in.
 */
export function EmailAttachments({
  attachments,
  onPrimary = false,
  onRemove,
  removeDisabled = false,
}: {
  attachments: ReadonlyArray<Attachment>;
  onPrimary?: boolean;
  /** Given, the corner drops the file instead of downloading it: a mail being
   *  written shows the same tiles as one that was sent. */
  onRemove?: (index: number) => void;
  removeDisabled?: boolean;
}) {
  if (attachments.length === 0) {
    return null;
  }

  return (
    <AttachmentGrid>
      {attachments.map((attachment, index) => (
        <AttachmentTile
          key={`${attachment.filename}:${index}`}
          attachment={attachment}
          onPrimary={onPrimary}
          onRemove={onRemove && (() => onRemove(index))}
          removeDisabled={removeDisabled}
        />
      ))}
    </AttachmentGrid>
  );
}

/** Exported for the caller whose tiles are not a list of data: the composer's
 *  files hold their own preview, so it builds the tiles and needs the row. */
export function AttachmentGrid({ children }: { children: ReactNode }) {
  return <div className="flex flex-wrap gap-x-2 gap-y-2.5">{children}</div>;
}

export function AttachmentTile({
  attachment,
  onPrimary = false,
  onRemove,
  removeDisabled = false,
}: {
  attachment: Attachment;
  onPrimary?: boolean;
  onRemove?: () => void;
  removeDisabled?: boolean;
}) {
  /** Dimmed when the tile can do nothing at all — no file behind it and no way
   *  to drop it. A file waiting to be uploaded has the second, so it reads as
   *  what it is: fine, just not stored yet. */
  const isDead = attachment.url === null && onRemove === undefined;
  const size = formatFileSize(attachment.size);
  const title = size ? `${attachment.filename} · ${size}` : attachment.filename;

  return (
    <div className={cn('relative flex w-18 flex-col', isDead && 'opacity-60')}>
      <AttachmentCard
        attachment={attachment}
        title={title}
        onPrimary={onPrimary}
      />
      <CornerAction
        attachment={attachment}
        onRemove={onRemove}
        removeDisabled={removeDisabled}
      />
    </div>
  );
}

/** One corner, one action: drop it where that is offered, download it where it
 *  is not, and nothing at all for a file that is no longer there. */
function CornerAction({
  attachment,
  onRemove,
  removeDisabled,
}: {
  attachment: Attachment;
  onRemove?: () => void;
  removeDisabled: boolean;
}) {
  const { t } = useTranslation();
  const position = 'absolute -right-1 -top-1';

  if (onRemove !== undefined) {
    return (
      <Button
        type="button"
        variant="outline"
        size="icon-xs"
        shape="pill"
        className={position}
        disabled={removeDisabled}
        onClick={onRemove}
        aria-label={t('Remove attachment')}
      >
        <X className="size-3.5" strokeWidth={2} />
      </Button>
    );
  }

  if (attachment.url === null) {
    return null;
  }

  return (
    <Button
      asChild
      variant="outline"
      size="icon-xs"
      shape="pill"
      className={position}
    >
      <a
        href={attachment.url}
        download={attachment.filename}
        aria-label={t('Download {{filename}}', {
          filename: attachment.filename,
        })}
      >
        <Download className="size-3.5" strokeWidth={2} />
      </a>
    </Button>
  );
}

/**
 * Preview and name are one target, because they are one thing: a name beside a
 * file that does not open it reads as a caption, and the click lands nowhere.
 *
 * `contentType` decides what the preview draws, not the extension: the name is
 * a label the sender chose and says nothing about the bytes.
 */
function AttachmentCard({
  attachment,
  onPrimary,
  title,
}: {
  attachment: Attachment;
  onPrimary: boolean;
  title: string;
}) {
  const isImage =
    attachment.url !== null &&
    attachment.contentType.toLowerCase().startsWith('image/');
  /** `rounded-lg` inside `rounded-xl` with `p-1` is the concentric pair:
   *  10px = 14px frame less its 4px padding. */
  const frameClassName = cn(
    'block rounded-xl p-1',
    onPrimary ? 'bg-white/15' : 'bg-black/5',
  );
  const preview = isImage ? (
    <img
      src={attachment.url ?? undefined}
      alt={attachment.filename}
      loading="lazy"
      decoding="async"
      className="size-16 rounded-lg object-cover"
    />
  ) : (
    <span
      className={cn(
        'flex size-16 items-center justify-center rounded-lg text-xs',
        onPrimary
          ? 'bg-white/25 text-primary-foreground/80'
          : 'bg-background text-muted-foreground',
      )}
    >
      {toExtensionLabel(attachment.filename)}
    </span>
  );
  const content = (
    <>
      <span className={frameClassName}>{preview}</span>
      <span
        className={cn(
          'min-w-0 truncate text-xs',
          attachment.url !== null && 'underline underline-offset-2',
          onPrimary ? 'text-primary-foreground' : 'text-primary',
        )}
      >
        {attachment.filename}
      </span>
    </>
  );

  if (attachment.url === null) {
    return (
      <span title={title} className="flex flex-col gap-2">
        {content}
      </span>
    );
  }

  return (
    <a
      href={attachment.url}
      target="_blank"
      rel="noreferrer"
      title={title}
      className="flex flex-col gap-2"
    >
      {content}
    </a>
  );
}

function toExtensionLabel(filename: string) {
  const parts = filename.split('.');
  const extension = parts.length > 1 ? parts[parts.length - 1] : '';

  return extension ? `.${extension.toLowerCase().slice(0, 5)}` : '—';
}
